import { Router } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app-core/services/data/data.service';
import { NotificationSettingService } from '@app-notification-setting/service/notification-setting.service';
import { catchError, debounceTime, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Notification_Configuration, notification } from '../models/notification-configuration.model';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { MyErrorStateMatcher } from '@app-core/constants/constants';
import {
  INPUT_KEYS,
  DIAGNOSTIC_EVENTS,
  OTHER_EVENTS,
  HARSH_EVENTS,
  AI_EVENTS,
  DISTRACTED_EVENTS,
  ALLOWED_DYNAMIC_FC,
  SAFETY_EVENTS,
} from '../constants/constants';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UserRoleManageService } from '@app-user-management/service/user-role-manage.service';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CourierNotificationComponent } from '@app-shared/components/courier-notification/courier-notification.component';
import { SuprsendNotificationComponent } from '@app-shared/components/suprsend-notification/suprsend-notification.component';
import { PreferencesResponse } from '@app-core/models/core.model';
import { MatSliderChange } from '@angular/material/slider';

@Component({
  selector: 'app-notification-setting-component',
  templateUrl: './notification-setting.component.html',
  styleUrls: ['./notification-setting.component.scss'],
  providers: [NotificationSettingService],
})
export class NotificationSettingComponent implements OnInit, OnDestroy {
  // Saves the intial notification configuration response. usefull for reseting
  public notificationConfigurationResponse: notification[];
  private _notificationConfiguration: notification[];
  public diagnosticEventsNotificationConfiguration: notification[];
  public harshEventsNotificationConfiguration: notification[];
  public aiEventsNotificationConfiguration: notification[];
  public distractedEventsNotificationConfiguration: notification[];
  public safetyEventsNotificationConfiguration: notification[];
  public otherEventsNotificationConfiguration: notification[];

  public get notificationConfiguration(): notification[] {
    return this._notificationConfiguration;
  }

  public set notificationConfiguration(config: notification[]) {
    this._notificationConfiguration = config;
    this.harshEventsNotificationConfiguration = this._notificationConfiguration.filter((config) =>
      (HARSH_EVENTS as string[]).includes(config.notificationType)
    );
    this.aiEventsNotificationConfiguration = this._notificationConfiguration.filter((config) =>
      (AI_EVENTS as string[]).includes(config.notificationType)
    );
    this.distractedEventsNotificationConfiguration =
      this._notificationConfiguration.filter((config) => (DISTRACTED_EVENTS as string[]).includes(config.notificationType)) || [];
    if (this.distractedEventsNotificationConfiguration.length > 0) {
      this.selectedDistractedEvent = this.distractedEventsNotificationConfiguration[0].notificationType;
      const active = this.distractedEventsNotificationConfiguration.some((config) => !!config.active);
      this.aiEventsNotificationConfiguration.unshift({ active: active, notificationType: 'DistractedDrivingGroupKey' });
    }
    this.safetyEventsNotificationConfiguration = [...this.harshEventsNotificationConfiguration, ...this.aiEventsNotificationConfiguration];
    this.diagnosticEventsNotificationConfiguration = this._notificationConfiguration.filter((config) =>
      DIAGNOSTIC_EVENTS.includes(config.notificationType)
    );
    this.otherEventsNotificationConfiguration = this._notificationConfiguration.filter((config) =>
      (OTHER_EVENTS as string[]).includes(config.notificationType)
    );
  }

  public notificationConfigurationLoader: Boolean = true;
  // Empty Subject used for trigger complete garbage collection
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public activeConfig = { key: 'active', controlType: 'toggle' };
  public form: FormGroup;
  public safetyEventsForm: FormGroup;
  public otherEventsForm: FormGroup;
  private fb = new FormBuilder();
  public searchNotificationConfiguration = new FormControl();
  public formLoader = false;
  public courierNotificationComponent: Promise<Component | typeof CourierNotificationComponent>;
  public suprSendNotificationComponent: Promise<Component | typeof SuprsendNotificationComponent>;
  public INPUT_KEYS = INPUT_KEYS;
  public ALLOWED_DYNAMIC_FC = ALLOWED_DYNAMIC_FC;
  public DISTRACTED_EVENTS = DISTRACTED_EVENTS;
  private notificationsList: string[] = [];
  public matcher = new MyErrorStateMatcher();
  public isDirty$: Observable<boolean> = of(false);
  private ssoUserHash: string = '';
  public currentMetricUnit = null;
  public selectedDistractedEvent;
  public shieldPlanEnabled: boolean = false;
  @ViewChild('idx') expansionPanel;

  constructor(
    public translate: TranslateService,
    public dialog: MatDialog,
    public router: Router,
    public accessService: AccessService,
    private snackBarService: SnackBarService,
    public dataService: DataService,
    public notificationSettingService: NotificationSettingService,
    private userService: UserRoleManageService
  ) {}

  ngOnInit(): void {
    this.ssoUserHash = this.accessService.getLoginInfo().userHash;
    this.notificationSettingService.markExposedEvents();

    this.dataService._currentFleet
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap(() => {
          const addDefaultValues$ = this.notificationSettingService.addDefaultValuesFromFleetConfig();
          const fleetPreferences$ = this.dataService.getFleetDetails().pipe(
            tap(
              (res: PreferencesResponse) => {
                const preferences = res?.preferences || null;
                this.shieldPlanEnabled = preferences?.shieldPlanType === 'SHIELD' ? true : false;
              },
              catchError(() => {
                this.shieldPlanEnabled = false;
                return of('');
              })
            )
          );
          return forkJoin([addDefaultValues$, fleetPreferences$]);
        })
      )
      .subscribe(() => this.getNotificationData());

    this.translate.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.createTranslationListForSearch();
    });

    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onSearchNotificationConfiguration() {
    const searchTerm = this.searchNotificationConfiguration?.value?.toLowerCase();
    if (!searchTerm) {
      this.notificationConfiguration = this.notificationConfigurationResponse;
      return;
    }
    const _notificationType = this.notificationsList.filter((notif) => notif.indexOf(searchTerm) > -1);
    this.notificationConfiguration = this.notificationConfigurationResponse.filter((config) => {
      const { notificationType } = config;
      const notificationTypeTranslation = this.translate.instant(`notificationConfiguration.${notificationType}`).toLowerCase();
      return _notificationType.indexOf(notificationTypeTranslation) > -1;
    });
  }

  clearSearchedReport() {
    this.searchNotificationConfiguration.reset();
    this.notificationConfiguration = this.notificationConfigurationResponse;
  }

  getNotificationData(): void {
    this.notificationConfigurationLoader = true;

    this.notificationSettingService
      .getConfiguration()
      .pipe(
        finalize(() => (this.notificationConfigurationLoader = false)),
        first()
      )
      .subscribe((res: Notification_Configuration) => {
        this.notificationConfigurationResponse = res?.configurations;
        this.notificationConfiguration = this.notificationConfigurationResponse;
        this.generateForm();
        this.createTranslationListForSearch();
      }, console.error);
  }

  onFormSubmit(forms: FormGroup[]) {
    this.formLoader = true;
    // Gets user details
    const { timezone, metricUnit, dateFormat, language } = this.accessService.getLoginInfo().userMetadata;
    const userPreferences = { timezone, metricUnit, dateFormat, language };

    // Gets form values
    let notificationConfigRaw;
    forms.forEach((form) => {
      const formValues = form.getRawValue();
      notificationConfigRaw = { ...notificationConfigRaw, ...formValues };
    });

    const configurations = Object.keys(notificationConfigRaw)
      .filter((key) => key !== 'DistractedDrivingGroupKey')
      .map((key) => {
        const { active, emailNotificationEnabled, tagIds, dynamicInputKey, inputKey, speed, duration, severity } =
          notificationConfigRaw[key];
        const tags = tagIds?.map(({ tagId }) => tagId);

        return {
          notificationType: key,
          active,
          emailNotificationEnabled,
          tagIds: tags,
          ...(dynamicInputKey ? { [dynamicInputKey]: inputKey } : {}),
          ...(speed && { speed: this.notificationSettingService.roundDecimal(speed) }),
          ...(duration && { duration: this.notificationSettingService.roundDecimal(duration) }),
          ...(severity && { severity: this.notificationSettingService.roundDecimal(severity) }),
        };
      });

    const notificationConfiguration = { configurations: configurations, userPreferences };
    return this.notificationSettingService.postConfiguration(notificationConfiguration).pipe(
      finalize(() => (this.notificationConfigurationLoader = false)),
      takeUntil(this.ngUnsubscribe),
      tap(() => {
        this.formLoader = false;
        this.expansionPanel.accordion.closeAll();
        forms.forEach((form) => form.markAsPristine());
        this.isDirty$ = of(this.form.dirty);
        this.snackBarService.success(this.translate.instant(`notificationConfiguration.formSuccess`));
      }),
      catchError(() => {
        this.formLoader = false;
        this.snackBarService.failure(this.translate.instant(`notificationConfiguration.formfailure`));
        return of([]);
      })
    );
  }

  onSubmit(forms: FormGroup[]) {
    this.onFormSubmit(forms).subscribe();
  }

  public closePanel(e: MatExpansionPanel): true {
    e.close();
    return true;
  }

  private generateForm() {
    try {
      // Initialize form groups for each type of event
      const notificationFormGroup = this.createFormGroupForEvents(DIAGNOSTIC_EVENTS, this.diagnosticEventsNotificationConfiguration);
      this.form = this.fb.group(notificationFormGroup);
      this.setupFormValueChanges(this.form);
      this.recommendedValueChangesDiagnosticEvents(this.form);

      const exposedSafetyEvents: string[] = [...this.notificationSettingService.exposedSafetyEvents];
      if (this.distractedEventsNotificationConfiguration.length > 0) {
        exposedSafetyEvents.unshift('DistractedDrivingGroupKey');
      }
      const safetyEventsFormGroup = this.createFormGroupForEvents(exposedSafetyEvents, this.safetyEventsNotificationConfiguration);
      this.safetyEventsForm = this.fb.group(safetyEventsFormGroup);
      this.setupFormValueChanges(this.safetyEventsForm);
      this.handleDistractedEventsFormChanges(this.safetyEventsForm);
      this.handleDistractedEventsStatusChanges(this.safetyEventsForm);
      this.recommendedValueChangesSafetyEvents(this.safetyEventsForm);

      const exposedOtherEvents = [...this.notificationSettingService.exposedOtherEvents];
      const otherEventsFormGroup = this.createFormGroupForEvents(exposedOtherEvents, this.otherEventsNotificationConfiguration);
      this.otherEventsForm = this.fb.group(otherEventsFormGroup);
      this.setupFormValueChanges(this.otherEventsForm);
    } catch (error) {
      console.error(error);
    }
  }

  private createFormGroupForEvents(events: string[], configurations: notification[]) {
    const formGroup = {};

    events.forEach((event) => {
      const config = configurations.find((config) => config.notificationType === event);
      if (!config) return;

      const key = config.notificationType;
      const inputKey = INPUT_KEYS[config.notificationType];

      // Prepare the form controls
      let formControls = {
        ...(inputKey?.key
          ? {
              dynamicInputKey: [inputKey.key],
              inputKey: [config[inputKey.key], [Validators.min(inputKey.min), Validators.max(inputKey.max), Validators.required]],
            }
          : {}),
        ...('active' in config ? { active: [config.active] } : {}),
        ...('emailNotificationEnabled' in config ? { emailNotificationEnabled: [config.emailNotificationEnabled] } : {}),
        ...('tagIds' in config ? { tagIds: [config.tagIds, this.maxTagsValidator(20)] } : {}),
        ...this.getDynamicFormControls(inputKey, config),
      };

      formGroup[key] = this.fb.group(formControls);
      this.addAdditionalKeys(inputKey, formGroup[key]);
    });

    return formGroup;
  }

  private getDynamicFormControls(inputKey, config) {
    if (!(inputKey?.allowedControls?.length > 0)) return {};

    const fg = {};
    inputKey.allowedControls.forEach((key) => {
      const allowedKeyConfig = inputKey[key];
      const defaultValue = config[key] || allowedKeyConfig?.defaultValue;
      const fc = new FormControl(defaultValue, [
        Validators.min(allowedKeyConfig.min),
        Validators.max(allowedKeyConfig.max),
        Validators.required,
      ]);
      fg[key] = fc;
    });

    return fg;
  }

  private addAdditionalKeys(inputKey, formGroup) {
    this.addIsRecommendedValueKey(inputKey, formGroup);
    inputKey.severity && this.addSeveritySliderValueKey(inputKey, formGroup.get('severity'));
  }

  private addIsRecommendedValueKey(inputKey, formGroup) {
    const formValue = formGroup.get('inputKey')?.value;
    if (inputKey?.key && inputKey?.recommended && formValue) {
      inputKey['isRecommendedValue'] = inputKey.recommended === formValue;
    }

    if (!(inputKey?.allowedControls?.length > 0)) return {};

    let isRecommendedValues = [];
    inputKey.allowedControls.forEach((key) => {
      const allowedKeyConfig = inputKey[key];
      const formValue = formGroup.get(key)?.value;
      const recommendedValue = formValue === allowedKeyConfig?.defaultValue;
      isRecommendedValues.push(recommendedValue);
    });

    if (isRecommendedValues?.length > 0) {
      inputKey.isRecommendedValue = isRecommendedValues.every((val) => !!val);
    }
  }

  private addSeveritySliderValueKey(inputKey, formControl) {
    const formValue = formControl?.value;

    if (formValue && inputKey.severity) {
      const max = inputKey.severity.max;
      const min = inputKey.severity.min;
      const sliderValue = max - (formValue - min);
      inputKey.severity.formValue = formValue;
      inputKey.severity.severitySliderValue = this.notificationSettingService.roundDecimal(sliderValue);
    }
  }

  private setupFormValueChanges(formGroup: FormGroup) {
    formGroup.markAllAsTouched();
    formGroup.valueChanges.pipe(debounceTime(1000), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      formGroup.markAsDirty();
      this.isDirty$ = of(formGroup.dirty);
    });
  }

  private handleDistractedEventsFormChanges(formGroup: FormGroup) {
    formGroup
      .get(['DistractedDrivingGroupKey', 'active'])
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((val) => {
        const exposedDistractionEvents = this.notificationSettingService.exposedSafetyEvents.filter((event) =>
          (DISTRACTED_EVENTS as string[]).includes(event)
        );
        exposedDistractionEvents.forEach((event) => {
          if (!INPUT_KEYS[event].readonly) {
            formGroup.get([event, 'active']).setValue(val);
          }
        });
      });
  }

  private handleDistractedEventsStatusChanges(formGroup: FormGroup) {
    const distractedGroupFc = formGroup.get('DistractedDrivingGroupKey');
    distractedGroupFc?.setValidators([this.distractedGroupValidator(formGroup)]);

    const exposedDistractionEvents = this.notificationSettingService.exposedSafetyEvents.filter((event) =>
      (DISTRACTED_EVENTS as string[]).includes(event)
    );
    exposedDistractionEvents.forEach((event) => {
      formGroup
        .get(event)
        ?.statusChanges.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          const isInvalid = this.isDistractedGroupInvalid(formGroup);
          isInvalid && distractedGroupFc?.setErrors({ subeventsInvalid: true });
          distractedGroupFc?.updateValueAndValidity();
        });
    });
  }

  private distractedGroupValidator(formGroup): ValidatorFn {
    return (): ValidationErrors | null => {
      const returnValue = formGroup && this.isDistractedGroupInvalid(formGroup) ? { subeventsInvalid: true } : null;
      return returnValue;
    };
  }

  private isDistractedGroupInvalid(formGroup: FormGroup) {
    const exposedDistractionEvents = this.notificationSettingService.exposedSafetyEvents.filter((event) =>
      (DISTRACTED_EVENTS as string[]).includes(event)
    );
    const isInvalid = exposedDistractionEvents
      .map((event) => {
        return !!formGroup.get(event)?.invalid;
      })
      .some((val) => val);
    return isInvalid;
  }

  private recommendedValueChangesDiagnosticEvents(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const eventForm = formGroup.get([key, 'inputKey']) as FormControl;
      eventForm?.valueChanges.pipe(debounceTime(100), takeUntil(this.ngUnsubscribe)).subscribe((val) => {
        const inputKey = INPUT_KEYS[key];
        if (inputKey && val && 'recommended' in inputKey) {
          inputKey['isRecommendedValue'] = inputKey.recommended === val ? true : false;
        }
      });
    });
  }

  private recommendedValueChangesSafetyEvents(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      INPUT_KEYS[key]?.allowedControls?.forEach((control) => {
        const fc = formGroup.get([key, control]) as FormControl;
        fc?.valueChanges.pipe(debounceTime(100), takeUntil(this.ngUnsubscribe)).subscribe(() => {
          this.checkRecommendedValues(INPUT_KEYS[key], formGroup.get(key) as FormGroup);
          if (control === 'severity') {
            this.addSeveritySliderValueKey(INPUT_KEYS[key], fc);
          }
        });
      });
    });
  }

  private checkRecommendedValues(inputKey, formGroup: FormGroup) {
    let isRecommendedValues = [];
    inputKey.allowedControls.forEach((control) => {
      const allowedKeyConfig = inputKey[control];
      const formValue = formGroup.get(control)?.value;
      const recommendedValue = formValue === allowedKeyConfig?.defaultValue ? true : false;
      isRecommendedValues.push(recommendedValue);
    });

    if (isRecommendedValues?.length > 0) {
      inputKey.isRecommendedValue = isRecommendedValues.every((val) => !!val);
    }
  }

  private createTranslationListForSearch() {
    // Creates translations list for search
    this.notificationsList = this.notificationConfigurationResponse?.map((config: notification) => {
      const { notificationType } = config;
      const inputKey = INPUT_KEYS[notificationType];
      const showDescription: boolean = inputKey?.allowedControls?.length > 0 || inputKey?.key?.length > 0;

      if (showDescription) {
        const notificationTypeTranslation = this.translate.instant(`notificationConfiguration.${notificationType}`);
        config.description = this.translate.instant('notificationConfiguration.description_DeviceOffline', {
          notificationType: notificationTypeTranslation,
        });
      }

      return this.translate.instant(`notificationConfiguration.${notificationType}`).toLowerCase();
    });
  }

  /**
   * @description assets tag selector function
   * @param notificationType
   * @returns
   */
  public selectedTags(form, notificationType) {
    return (tagIds) => {
      // check if the existing value is same as updated value
      const existingtags = form.get([notificationType, 'tagIds'])?.value.map(({ tagId }) => tagId);

      let isSameTags = tagIds.sort().toString() === existingtags.sort().toString();
      if (isSameTags) return;

      // updates tagId in form
      tagIds = tagIds.filter((tag) => !!tag);
      form.markAsDirty();
      form.get([notificationType, 'tagIds'])?.patchValue(tagIds.map((id) => ({ tagId: id })));
    };
  }

  private maxTagsValidator(limit: number) {
    return (control: FormControl) => {
      return control.value && control.value.length > limit ? { maxTagsLimit: true } : null;
    };
  }

  public openConfirmation() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      position: {
        top: '24px',
      },
      autoFocus: false,
      disableClose: true,
      panelClass: ['mobile-modal'],
      data: {
        title: this.translate.instant('notificationConfigurationConfirm.title'),
        content: `${this.translate.instant('notificationConfigurationConfirm.content')}`,
        actions: [
          { actionButton: this.translate.instant('notificationConfigurationConfirm.cancel'), dialogCloseValue: false },
          { actionButton: this.translate.instant('notificationConfigurationConfirm.save'), dialogCloseValue: true, primary: true },
        ],
      },
    });
    return dialogRef.afterClosed().pipe(
      switchMap((confirmSave: boolean = false) => {
        if (confirmSave) {
          return this.onFormSubmit([this.form, this.safetyEventsForm, this.otherEventsForm]).pipe(map(() => true));
        } else {
          return of(true);
        }
      })
    );
  }

  public enableEmailNotification(e: MatCheckboxChange, formControl: FormControl) {
    if (e.checked) {
      if (this.ssoUserHash) {
        this.handleSSOUserConsent(formControl);
      }
    }
  }

  private handleSSOUserConsent(formControl: FormControl) {
    this.userService
      .getConsentTimeFromSSOUser(encodeURIComponent(this.ssoUserHash))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (consentTime: string) => {
          if (!consentTime) {
            this.handleConsentDialog(formControl);
          }
        },
        (error) => {
          if (error.status === 404) {
            // Handle 404 error by opening the consent dialog
            this.handleConsentDialog(formControl);
          }
        }
      );
  }

  private handleConsentDialog(formControl: FormControl) {
    const ssoConsentDialogRef = this.notificationSettingService.openSSOConsentDialog();
    ssoConsentDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((consent: boolean = false) => {
        if (!consent) {
          formControl.setValue(false);
        }
      });
  }

  public onToggleSelect(event) {
    this.selectedDistractedEvent = event?.value?.notificationType || this.distractedEventsNotificationConfiguration?.[0].notificationType;
  }

  /**
   * Prevent inputting inputs other than Number, backspace
   * @param event Event
   */
  public onKeyPress(event: KeyboardEvent, allowFloat) {
    const key = event.key;
    if (key !== 'Backspace' && key !== 'Delete' && !/^\d$/.test(key)) {
      if (allowFloat && key === '.') {
        return;
      }
      event.preventDefault(); // Prevent invalid keys
    }
  }

  public applyRecommendedSetting(form, notificationType) {
    if (DIAGNOSTIC_EVENTS.includes(notificationType)) {
      form.get([notificationType, 'inputKey']).setValue(INPUT_KEYS[notificationType].recommended);
    } else if (SAFETY_EVENTS.includes(notificationType)) {
      INPUT_KEYS[notificationType]?.allowedControls?.forEach((control) => {
        const config = INPUT_KEYS[notificationType]?.[control];
        const fc = form.get([notificationType, control]);
        if (config && fc) {
          fc.setValue(config.defaultValue);
        }
      });
    }
  }

  public showApplyRecommendedSetting(notificationType) {
    return 'isRecommendedValue' in INPUT_KEYS[notificationType] && INPUT_KEYS[notificationType]?.isRecommendedValue === false;
  }

  public onSeverityChange(event: MatSliderChange, formGroup: FormGroup, notificationType) {
    const fc = formGroup.get([notificationType, 'severity']);
    const max = INPUT_KEYS[notificationType].severity.max;
    const min = INPUT_KEYS[notificationType].severity.min;
    const sliderValue = event?.value;
    const formValue = min + (max - sliderValue);
    const parsedValue = this.notificationSettingService.roundDecimal(formValue);
    INPUT_KEYS[notificationType].severity['formValue'] = formValue;
    fc.setValue(parsedValue);
    // sv = max - (fc-min)
    // fc = min + (max - sv)
  }
}
